import React, { useEffect, useState } from 'react';
import {
  FormattedMessage, useIntl,
} from 'react-intl';
import {
  Alert,
  Badge,
  DescriptionList,
  Heading,
  LinkAsButton,
  Panel,
  Spinner,
  TextField,
} from '@printdeal/ui-library';
import { navigate } from 'gatsby';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';
import useGetMandatesQuery from '../../hooks/mandates/useGetMandatesQuery';
import { Mandate } from '../../../../../services/payment/PaymentV2Service';
import MandateCallbackAlerts from './components/MandateCallbackAlerts';
import useCancelMandateMutation from '../../hooks/mandates/useCancelMandateMutation';
import MandateIssuerIcon from './components/MandateIssuerIcon';
import usePrefetchMandateIssuers from '../../hooks/mandates/usePrefetchMandateIssuers';
import { Money } from '../../../../Money';

const MandatesPage = () => {
  usePrefetchMandateIssuers();

  const intl = useIntl();
  const getMandatesQuery = useGetMandatesQuery();
  const cancelMandateMutation = useCancelMandateMutation();

  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [searchedMandates, setSearchedMandates] = useState<Mandate[]>([]);
  const [pendingMandates, setPendingMandates] = useState<number>(0);

  useEffect(() => {
    if (getMandatesQuery.data) {
      const filteredMandates = getMandatesQuery.data.filter((mandate) => mandate.status === 'Success');
      const mandates = filteredMandates.filter((mandate) => {
        if (searchQuery) {
          const searchFields = [mandate.iban, mandate.bankId, mandate.signerName];
          return searchFields.some((field) => field?.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return true;
      });

      const pendingStatusMandates = getMandatesQuery.data.filter((mandate) => mandate.status === 'Pending');
      setPendingMandates(pendingStatusMandates.length);

      setSearchedMandates(mandates);
    }
  }, [searchQuery, getMandatesQuery.data]);

  useEffect(() => {
    if (getMandatesQuery.error?.response?.status === 404) {
      setSearchedMandates([]);
      setPendingMandates(0);
    }
  }, [getMandatesQuery.error]);

  return (
    <>
      <Head title={intl.formatMessage({ id: 'account.mandates.page_title' })} />
      <div className="pd-flex pd-flex-col pd-gap-4">
        <Breadcrumb>
          <Breadcrumb.Item to=".." key="account.index.title">
            <FormattedMessage id="account.index.title" defaultMessage="My account" />
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage key="account.mandates.page_title">
            <FormattedMessage id="account.mandates.page_title" defaultMessage="My mandates" />
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className="pd-flex pd-items-center pd-justify-between">
          <Heading as="h2">
            <FormattedMessage id="account.mandates.page_title" defaultMessage="My mandates" />
          </Heading>
          <LinkAsButton
            variant="secondary"
            iconBefore="plus"
            onClick={() => navigate('add')}
            className="pd-text-sm"
          >
            <FormattedMessage id="account.mandates.add" defaultMessage="Add mandate" />
          </LinkAsButton>
        </div>

        {pendingMandates > 0 && (
          <Alert variant="info">
            <FormattedMessage
              id="account.mandates.pendingMandates"
              defaultMessage="We have successfully received the application for a continuous authorization, which is currently being processed by your bank. As soon as it is active, you can see it here in the overview and choose it as a payment method when placing your order"
            />
          </Alert>
        )}

        <TextField
          id="mandate-search-field"
          name="mandate-search-field"
          label={intl.formatMessage({ id: 'account.mandates.searchBarLabel', defaultMessage: 'Search' })}
          placeholder={intl.formatMessage({ id: 'account.mandates.searchBarPlaceholder', defaultMessage: 'For example: NL13TEST0123456789' })}
          iconAfter="search"
          value={searchQuery}
          onChange={(event) => {
            if (!event.target.value) {
              setSearchQuery(undefined);
              return;
            }
            setSearchQuery(event.target.value);
          }}
        />

        <MandateCallbackAlerts />

        {cancelMandateMutation.isError && (
          <Alert variant="error">
            <FormattedMessage id="account.mandates.cancel.error" defaultMessage="Error canceling mandate" />
          </Alert>
        )}

        {getMandatesQuery.isError && getMandatesQuery.error?.response?.status !== 404 && (
          <Alert variant="error">
            <FormattedMessage id="account.mandates.list.error" defaultMessage="Error getting mandates" />
          </Alert>
        )}

        {getMandatesQuery.isLoading && (
          <div className="pd-flex pd-justify-center pd-p-10">
            <Spinner size="lg" />
          </div>
        )}

        {!getMandatesQuery.isLoading && !searchedMandates.length && (
          <Alert variant="dim">
            <div className="pd-flex pd-justify-center">
              <FormattedMessage id="account.mandates.noMandates" defaultMessage="Unfortunately, we can't find any mandates" />
            </div>
          </Alert>
        )}

        {searchedMandates && searchedMandates.map((mandate) => (
          <Panel className="pd-p-0" key={mandate.mandateId}>
            <div className="pd-p-6">
              <DescriptionList className="pd-gap-x-6 pd-gap-y-1 pd-text-sm">
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.mandates.statusbadge" defaultMessage="Status" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>
                    <Badge variant="success">
                      <FormattedMessage id="account.mandates.status.success" defaultMessage="Success" />
                    </Badge>
                  </DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.mandates.signerName" defaultMessage="Name" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>{mandate.signerName}</DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.mandates.iban" defaultMessage="IBAN" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>{mandate.iban}</DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title className="pd-flex pd-items-center">
                    <FormattedMessage id="account.mandates.bic" defaultMessage="BIC" />
                  </DescriptionList.Title>
                  <DescriptionList.Content className="pd-flex pd-items-center pd-gap-2">
                    <p>{mandate.bankId}</p>
                    <MandateIssuerIcon issuerId={mandate.bankId} />
                  </DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.mandates.maximum.amount" defaultMessage="Max amount" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>
                    <Money
                      value={Math.round(mandate.maxAmount * 100)}
                      currency="EUR"
                    />
                  </DescriptionList.Content>
                </>
              </DescriptionList>
            </div>
            <div className="pd-flex pd-justify-end pd-gap-4 pd-rounded-b-md pd-border-t-1 pd-bg-dim-100 pd-px-6 pd-py-4">
              <LinkAsButton
                variant="secondary"
                iconBefore="times"
                isLoading={cancelMandateMutation.isPending && cancelMandateMutation.variables === mandate.mandateId}
                disabled={cancelMandateMutation.isPending || mandate.status !== 'Success'}
                onClick={async () => {
                  const redirectUrl = await cancelMandateMutation.mutateAsync(mandate.mandateId);

                  navigate(redirectUrl);
                }}
                className="pd-text-sm"
              >
                <FormattedMessage id="account.mandates.deactivate" defaultMessage="Deactivate" />
              </LinkAsButton>
            </div>
          </Panel>
        ))}

        <div>
          <LinkAsButton
            variant="secondary"
            iconBefore="chevron-left"
            onClick={() => navigate('..')}
            className="pd-text-sm"
          >
            <FormattedMessage id="account.linkToAccount" defaultMessage="Link to account" />
          </LinkAsButton>
        </div>
      </div>
    </>
  );
};

export default MandatesPage;
